<template>
  <themed-modal
    name="create-proposal"
    title="Create Proposal"
    size="lg"
    @close="handleClose"
  >
    <form @submit.prevent="handleSubmit">
      <div class="create-proposal-form">
        <!-- Building Selection -->
        <div class="form-group">
          <label for="building_id" class="form-label required">Building</label>
          <select
            id="building_id"
            v-model="form.building_id"
            class="form-select"
            :class="{ 'is-invalid': errors.building_id }"
          >
            <option value="">Select Building</option>
            <optgroup v-if="existingSites.length" label="Existing Sites">
              <option
                v-for="site in existingSites"
                :key="`site-${site.id}`"
                :value="site.building_id"
              >
                {{ site.building.address_line_1 }}, {{ site.building.city }}, {{ site.building.state }}
              </option>
            </optgroup>
            <optgroup v-if="comps.length" label="Comps">
              <option
                v-for="comp in comps"
                :key="`comp-${comp.id}`"
                :value="comp.id"
              >
                {{ comp.address_line_1 }}, {{ comp.city }}, {{ comp.state }}
              </option>
            </optgroup>
          </select>
          <div v-if="errors.building_id" class="form-error">
            {{ errors.building_id[0] }}
          </div>
        </div>

        <!-- Form Fields Grid -->
        <div class="form-grid">
          <!-- Rentable Sq. Ft. -->
          <div class="form-group">
            <label for="total_square_feet" class="form-label">Rentable Sq. Ft.</label>
            <input
              id="total_square_feet"
              v-model.number="form.total_square_feet"
              type="number"
              class="form-input text-right"
              :class="{ 'is-invalid': errors.total_square_feet }"
            />
            <div v-if="errors.total_square_feet" class="form-error">
              {{ errors.total_square_feet[0] }}
            </div>
          </div>

          <!-- Term (Years) -->
          <div class="form-group">
            <label for="term" class="form-label">Term (Years)</label>
            <input
              id="term"
              v-model.number="form.term"
              type="number"
              class="form-input text-right"
              :class="{ 'is-invalid': errors.term }"
            />
            <div v-if="errors.term" class="form-error">
              {{ errors.term[0] }}
            </div>
          </div>

          <!-- Loss Factor (%) -->
          <div class="form-group">
            <label for="loss_factor_percentage" class="form-label">Loss Factor (%)</label>
            <input
              id="loss_factor_percentage"
              v-model.number="form.loss_factor_percentage"
              type="number"
              step="1"
              class="form-input text-right"
              :class="{ 'is-invalid': errors.loss_factor_percentage }"
            />
            <div v-if="errors.loss_factor_percentage" class="form-error">
              {{ errors.loss_factor_percentage[0] }}
            </div>
          </div>

          <!-- Discount Rate (%) -->
          <div class="form-group">
            <label for="discount_rate" class="form-label">Discount Rate (%)</label>
            <input
              id="discount_rate"
              v-model.number="form.discount_rate"
              type="number"
              step="1"
              class="form-input text-right"
              :class="{ 'is-invalid': errors.discount_rate }"
            />
            <div v-if="errors.discount_rate" class="form-error">
              {{ errors.discount_rate[0] }}
            </div>
          </div>

          <!-- Include Free Months -->
          <div class="form-group">
            <label for="include_free_months" class="form-label">Incl. Free Months</label>
            <select
              id="include_free_months"
              v-model="form.include_free_months"
              class="form-select"
              :class="{ 'is-invalid': errors.include_free_months }"
            >
              <option value="">Select</option>
              <option :value="0">No</option>
              <option :value="1">Yes</option>
            </select>
            <div v-if="errors.include_free_months" class="form-error">
              {{ errors.include_free_months[0] }}
            </div>
          </div>

          <!-- TI $ Req. PSF -->
          <div class="form-group">
            <label for="ti_required_psf" class="form-label">TI $ Req. PSF</label>
            <input
              id="ti_required_psf"
              v-model.number="form.ti_required_psf"
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g. 7.50"
              class="form-input text-right"
              :class="{ 'is-invalid': errors.ti_required_psf }"
            />
            <small class="form-hint">Enter dollar amount per sq. ft. (e.g. 7.50)</small>
            <div v-if="errors.ti_required_psf" class="form-error">
              {{ errors.ti_required_psf[0] }}
            </div>
          </div>

          <!-- TI $ Offered PSF -->
          <div class="form-group">
            <label for="ti_offered_psf" class="form-label">TI $ Offered PSF</label>
            <input
              id="ti_offered_psf"
              v-model.number="form.ti_offered_psf"
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g. 5.90"
              class="form-input text-right"
              :class="{ 'is-invalid': errors.ti_offered_psf }"
            />
            <small class="form-hint">Enter dollar amount per sq. ft. (e.g. 5.90)</small>
            <div v-if="errors.ti_offered_psf" class="form-error">
              {{ errors.ti_offered_psf[0] }}
            </div>
          </div>
        </div>
      </div>
    </form>

    <template #footer>
      <themed-button
        variant="secondary"
        @click="handleClose"
      >
        Cancel
      </themed-button>

      
      <themed-button
        variant="default"
        :loading="isSaving"
        @click="handleSubmit"
      >
        Create Proposal
      </themed-button>
    </template>
  </themed-modal>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
  name: 'CreateProposalModal',
  
  components: {
    ThemedModal,
    ThemedButton
  },
  
  props: {
    projectId: {
      type: Number,
      required: true
    }
  },
  
  data() {
    return {
      form: {
        project_id: this.projectId,
        building_id: '',
        revision_number: 1,
        total_square_feet: null,
        term: null,
        loss_factor_percentage: null,
        discount_rate: null,
        include_free_months: '',
        ti_required_psf: null,
        ti_offered_psf: null
      },
      isSaving: false,
      errors: {}
    };
  },
  
  computed: {
    ...mapState(['projects']),
    ...mapGetters(['compsSortedBy']),
    
    /**
     * Get existing sites for the current project
     */
    existingSites() {
      if (this.projects.selectedProject && this.projects.selectedProject.sites) {
        return this.projects.selectedProject.sites;
      }
      return [];
    },
    
    /**
     * Get comps for the current project
     */
    comps() {
      return this.compsSortedBy || [];
    }
  },
  
  methods: {
    /**
     * Validates the form before submission
     * @returns {Boolean} True if form is valid
     */
    validateForm() {
      this.errors = {};
      let isValid = true;
      
      if (!this.form.building_id) {
        this.errors.building_id = ['Building is required'];
        isValid = false;
      }
      
      return isValid;
    },
    
    /**
     * Handles form submission
     */
    async handleSubmit() {
      // Validate form
      if (!this.validateForm()) {
        return;
      }
      
      this.isSaving = true;
      
      try {
        // Use Vuex action to create proposal - it returns the created proposal
        await this.$store.dispatch('createProposal', this.form);
        
        // Show success notification
        if (this.$toasted) {
          this.$toasted.success('Proposal created successfully');
        }
        
        // Close modal
        this.$modal.hide('create-proposal');
        
        // Reset form
        this.resetForm();
        
        // Reload page to refresh proposals table and count
        setTimeout(() => {
          window.location.reload();
        }, 500);
        
      } catch (error) {
        // Handle validation errors from API
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          // Show generic error message
          if (this.$toasted) {
            this.$toasted.error('Failed to create proposal. Please try again.');
          }
        }
      } finally {
        this.isSaving = false;
      }
    },
    
    /**
     * Handles modal close
     */
    handleClose() {
      this.resetForm();
      this.$modal.hide('create-proposal');
    },
    
    /**
     * Resets the form to initial state
     */
    resetForm() {
      this.form = {
        project_id: this.projectId,
        building_id: '',
        revision_number: 1,
        total_square_feet: null,
        term: null,
        loss_factor_percentage: null,
        discount_rate: null,
        include_free_months: '',
        ti_required_psf: null,
        ti_offered_psf: null
      };
      this.errors = {};
    }
  }
};
</script>
